'use client';

import { useEffect, useRef, useState } from 'react';

import dayjs from '../../utils/dayjs';

import AgeGateContext from './context';
import AgeLimit from './age-limit';
import constants from './constants';

type AgeGateWrapperProps = React.PropsWithChildren<{
  allowedContent: React.ReactNode;
  localStorageKey?: string;
  isPublicPage?: boolean;
  ageLimitProps?: React.ComponentProps<typeof AgeLimit>;
  ageLimit?: number;
  alwaysVisible?: boolean;
}>;

const DEFAULT_LOCAL_STORAGE_KEY = 'userBirthdate';

export default function AgeGateWrapper(props: AgeGateWrapperProps) {
  const [birthdate, setBirthdate] = useState(
    localStorage.getItem(props.localStorageKey || DEFAULT_LOCAL_STORAGE_KEY) || '',
  );
  const birthdateChecked = useRef(false);

  if (!birthdateChecked.current) {
    birthdateChecked.current = true;
    const date = dayjs(birthdate, 'DD-MM-YYYY', true);
    if (
      !date.isValid() ||
      dayjs().diff(date, 'year') < (props.ageLimit || constants.DEFAULT_AGE_LIMIT)
    ) {
      localStorage.removeItem(props.localStorageKey || DEFAULT_LOCAL_STORAGE_KEY);
      setBirthdate('');
    }
  }

  useEffect(() => {
    localStorage.setItem(props.localStorageKey || DEFAULT_LOCAL_STORAGE_KEY, birthdate);
  }, [birthdate]);

  if (props.isPublicPage) return props.allowedContent; // Skip age gate on public pages

  return (
    <AgeGateContext.Provider value={{ birthdate, setBirthdate }}>
      <AgeGateContext.Consumer>
        {({ birthdate: contextBirthdate }) => {
          const date = dayjs(contextBirthdate, 'DD-MM-YYYY', true);

          let component: React.ReactNode;

          if (!date.isValid()) {
            component = props.children;
          } else if (dayjs().diff(date, 'year') < (props.ageLimit || constants.DEFAULT_AGE_LIMIT)) {
            component = <AgeLimit {...(props.ageLimitProps || {})} />;
          } else if (props.alwaysVisible) {
            component = props.children;
          } else {
            component = props.allowedContent;
          }

          return component;
        }}
      </AgeGateContext.Consumer>
    </AgeGateContext.Provider>
  );
}
